import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/** Scent filters progressively enhance a complete Liquid-rendered catalogue. */
class FragranceLibrary extends Component {
  /** @type {AbortController | undefined} */
  controller;
  /** @type {ResizeObserver | undefined} */
  carouselObserver;

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.controller?.abort();
    this.carouselObserver?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const filters = this.querySelector('[data-ia-filters]');
    // A paginated library keeps complete server navigation instead of partial filters.
    if (!(filters instanceof HTMLElement) || this.querySelector('.ia-pagination')) return;
    const cards = [...this.querySelectorAll('.ia-card')].filter((card) => card instanceof HTMLElement);
    if (!cards.length) return;
    const grid = this.querySelector('[data-ia-grid]');
    const fitCarousel = () => {
      if (!(grid instanceof HTMLElement)) return;
      if (window.getComputedStyle(grid).display !== 'flex') {
        grid.style.removeProperty('--ia-carousel-height');
        return;
      }
      const bounds = grid.getBoundingClientRect();
      const visible = cards.filter((card) => {
        const box = card.getBoundingClientRect();
        return !card.hidden && box.right > bounds.left + 1 && box.left < bounds.right - 1;
      });
      const height = Math.max(0, ...visible.map((card) => card.offsetHeight));
      if (height) grid.style.setProperty('--ia-carousel-height', `${height + 20}px`);
    };
    grid?.addEventListener('scroll', fitCarousel, { signal, passive: true });
    this.addEventListener('load', fitCarousel, { signal, capture: true });
    window.addEventListener('resize', fitCarousel, { signal });
    if (grid instanceof HTMLElement && typeof ResizeObserver !== 'undefined') {
      this.carouselObserver = new ResizeObserver(fitCarousel);
      this.carouselObserver.observe(grid);
      for (const card of cards) this.carouselObserver.observe(card);
    }
    const formats = this.querySelector('[data-ia-format-filters]');
    const formatValues = ['all', 'perfume', 'attar'];
    const familyValues = ['all', 'fresh', 'floral', 'woody', 'warm', 'musky'];
    const currentUrl = new URL(window.location.href);
    let format = currentUrl.searchParams.get('format') ?? 'all';
    let family = currentUrl.searchParams.get('scent') ?? 'all';
    if (!formatValues.includes(format) || !(formats instanceof HTMLElement)) format = 'all';
    if (!familyValues.includes(family)) family = 'all';
    filters.hidden = false;
    if (formats instanceof HTMLElement) formats.hidden = false;

    const apply = (updateUrl = false) => {
      let count = 0;
      for (const card of cards) {
        const matchesFormat = format === 'all' || card.dataset.format === format;
        const matchesFamily = family === 'all' || card.dataset.scentFamilies?.split(' ').includes(family);
        card.hidden = !matchesFormat || !matchesFamily;
        if (!card.hidden) count++;
      }
      for (const filter of filters.querySelectorAll('[data-filter]')) {
        if (!(filter instanceof HTMLButtonElement)) continue;
        filter.setAttribute('aria-pressed', String(filter.dataset.filter === family));
        filter.hidden = filter.dataset.filter !== 'all' && !cards.some((card) =>
          (format === 'all' || card.dataset.format === format) && card.dataset.scentFamilies?.split(' ').includes(filter.dataset.filter ?? '')
        );
      }
      for (const filter of formats?.querySelectorAll('[data-format]') ?? []) {
        if (filter instanceof HTMLButtonElement) filter.setAttribute('aria-pressed', String(filter.dataset.format === format));
      }
      const counter = this.querySelector('[data-ia-count]');
      if (counter instanceof HTMLElement) {
        const template = count === 1 ? counter.dataset.countOne : counter.dataset.countTemplate;
        counter.textContent = template?.replace('__COUNT__', String(count)) ?? '';
      }
      const empty = this.querySelector('[data-ia-no-results]');
      if (empty instanceof HTMLElement) empty.hidden = count > 0;
      if (updateUrl) {
        if (grid instanceof HTMLElement) grid.scrollLeft = 0;
      }
      fitCarousel();
      if (updateUrl && ['https:', 'http:'].includes(window.location.protocol)) {
        const url = new URL(window.location.href);
        if (format === 'all') url.searchParams.delete('format');
        else url.searchParams.set('format', format);
        if (family === 'all') url.searchParams.delete('scent');
        else url.searchParams.set('scent', family);
        window.history.replaceState({}, '', url);
      }
    };
    filters.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-filter]');
      if (!(button instanceof HTMLButtonElement)) return;
      family = button.dataset.filter ?? 'all';
      apply(true);
    }, { signal });
    formats?.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-format]');
      if (!(button instanceof HTMLButtonElement)) return;
      format = button.dataset.format ?? 'all';
      apply(true);
    }, { signal });
    for (const entry of this.querySelectorAll('[data-format-target]')) {
      entry.addEventListener('click', (event) => {
        if (!(entry instanceof HTMLAnchorElement)) return;
        event.preventDefault();
        format = entry.dataset.formatTarget ?? 'all';
        family = 'all';
        apply(true);
        const library = this.querySelector('#IbnLibrary');
        library?.scrollIntoView({ block: 'start' });
        if (library instanceof HTMLElement) library.focus({ preventScroll: true });
      }, { signal });
    }
    apply();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.carouselObserver?.disconnect();
    super.disconnectedCallback();
  }
}

/**
 * Product controls keep Shopify's gallery and cart event contracts intact.
 * Prices and rules are formatted by Liquid for the active market, not recalculated.
 */
class FragranceProduct extends Component {
  /** @type {AbortController | undefined} */
  controller;
  /** @type {AbortController | undefined} */
  request;
  /** @type {AbortController | undefined} */
  galleryRequest;
  /** @type {Map<string, HTMLElement>} */
  galleryCache = new Map();
  /** @type {IntersectionObserver | undefined} */
  purchaseObserver;
  /** @type {HTMLStyleElement | undefined} */
  chatSpacingStyle;
  busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.controller?.abort();
    this.galleryRequest?.abort();
    this.galleryCache.clear();
    this.purchaseObserver?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const select = this.querySelector('[data-ia-select]');
    if (!(select instanceof HTMLSelectElement)) return;
    const variants = this.querySelector('[data-ia-variants]');
    if (variants instanceof HTMLElement) {
      variants.hidden = false;
      select.hidden = true;
      select.tabIndex = -1;
      // The select is still the native form's authoritative variant ID.
      variants.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || this.busy) return;
        const button = event.target.closest('[data-variant-id]');
        if (!(button instanceof HTMLButtonElement)) return;
        select.value = button.dataset.variantId ?? '';
        this.selectVariant(select);
      }, { signal });
    }
    select.addEventListener('change', () => this.selectVariant(select), { signal });
    this.addEventListener('submit', this.addToBag, { signal });
    this.selectVariant(select, false);
    // Eaze renders its fixed button inside an open shadow root.
    customElements.whenDefined('gty-whatsapp-chat-button').then(() => {
      if (signal.aborted || !this.isConnected) return;
      const root = document.querySelector('gty-whatsapp-chat-button')?.shadowRoot;
      if (!root || this.chatSpacingStyle?.isConnected) return;
      const style = document.createElement('style');
      style.textContent = '.gty-wacb-wrapper { bottom: var(--ia-chat-bottom, 16px) !important; }';
      root.append(style);
      this.chatSpacingStyle = style;
    });
    const purchase = this.querySelector('[data-ia-purchase]');
    const sticky = this.querySelector('[data-ia-sticky-buy]');
    if (purchase instanceof HTMLElement && sticky instanceof HTMLElement && typeof IntersectionObserver !== 'undefined') {
      this.purchaseObserver = new IntersectionObserver(([entry]) => {
        if (entry) sticky.hidden = entry.isIntersecting;
      }, { threshold: 0 });
      this.purchaseObserver.observe(purchase);
      sticky.querySelector('a')?.addEventListener('click', () => {
        const target = this.querySelector('.ia-buy-anchor');
        if (target instanceof HTMLElement) target.focus({ preventScroll: true });
      }, { signal });
    }
  }

  /** @param {HTMLSelectElement} select @param {boolean} [updateUrl] */
  selectVariant(select, updateUrl = true) {
    const variant = select.selectedOptions[0];
    if (!variant) return;
    const data = variant.dataset;
    for (const price of this.querySelectorAll('[data-ia-price]')) price.textContent = data.price ?? '';
    const compare = this.querySelector('[data-ia-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = data.compare ?? '';
      compare.hidden = !data.compare;
    }
    for (const selected of this.querySelectorAll('[data-ia-selected]')) selected.textContent = data.title === 'Default Title' ? '' : data.title ?? '';
    for (const add of this.querySelectorAll('[data-ia-add]')) {
      if (add instanceof HTMLButtonElement) add.disabled = data.available !== 'true' || this.busy;
    }
    for (const label of this.querySelectorAll('[data-ia-add-label]')) label.textContent = (data.available === 'true' ? this.dataset.add : this.dataset.unavailable) ?? '';
    for (const button of this.querySelectorAll('[data-variant-id]')) {
      if (button instanceof HTMLButtonElement) button.setAttribute('aria-pressed', String(button.dataset.variantId === variant.value));
    }
    const quantity = this.querySelector('input[name="quantity"]');
    if (quantity instanceof HTMLInputElement) {
      quantity.min = data.min ?? '1';
      quantity.step = data.step ?? '1';
      if (data.max) quantity.max = data.max;
      else quantity.removeAttribute('max');
      const min = Number(quantity.min);
      const step = Number(quantity.step);
      let value = Math.max(min, Number(quantity.value) || min);
      value = min + Math.floor((value - min) / step) * step;
      if (data.max) value = Math.min(value, Number(data.max));
      quantity.value = String(value);
      quantity.disabled = data.available !== 'true';
      for (const control of quantity.closest('quantity-selector-component')?.querySelectorAll('button') ?? []) {
        control.disabled = data.available !== 'true';
      }
    }
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.value);
      if (window.location.protocol === 'https:' || window.location.protocol === 'http:') {
        window.history.replaceState({}, '', url);
      }
      if (this.dataset.sectionId) this.refreshGallery(variant.value);
      else this.showVariantMedia(data.mediaId);
      const status = this.querySelector('[data-ia-status]');
      if (status) status.textContent = '';
    }
  }

  /**
   * Render the native gallery for the selected variant, like the theme picker.
   * Replace only the gallery so the focused format button and purchase form stay put.
   * @param {string} variantId
   */
  async refreshGallery(variantId) {
    this.galleryRequest?.abort();
    const body = this.querySelector('[data-ia-gallery-body]');
    const status = this.querySelector('[data-ia-gallery-status]');
    if (!(body instanceof HTMLElement)) return;
    const request = new AbortController();
    this.galleryRequest = request;
    const apply = (/** @type {HTMLElement} */ source) => {
      body.replaceChildren(...source.cloneNode(true).childNodes);
      body.dataset.galleryVariant = variantId;
      body.hidden = false;
      body.removeAttribute('aria-busy');
      if (status) status.textContent = '';
    };
    if (body.dataset.galleryVariant === variantId) {
      body.hidden = false;
      body.removeAttribute('aria-busy');
      if (status) status.textContent = '';
      return;
    }
    const cached = this.galleryCache.get(variantId);
    if (cached) { apply(cached); return; }
    body.hidden = true;
    body.setAttribute('aria-busy', 'true');
    if (status) status.textContent = this.dataset.galleryLoading ?? '';
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');
    const timeout = window.setTimeout(() => request.abort('timeout'), 15000);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Gallery response failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-ia-gallery-body]');
      if (!(next instanceof HTMLElement)) throw new Error('Gallery missing');
      if (request.signal.aborted || !this.isConnected) return;
      this.galleryCache.set(variantId, next);
      apply(next);
    } catch {
      if (!this.isConnected || this.galleryRequest !== request) return;
      body.removeAttribute('aria-busy');
      if (status) {
        status.textContent = `${this.dataset.galleryError ?? ''} `;
        const link = document.createElement('a');
        url.searchParams.delete('section_id');
        link.href = url.href;
        link.textContent = this.dataset.galleryRetry ?? '';
        status.append(link);
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /** @param {string | undefined} mediaId */
  showVariantMedia(mediaId) {
    if (!mediaId) return;
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.querySelector('media-gallery'));
    if (!gallery?.media) return;
    const index = gallery.media.findIndex((media) => media.querySelector('[data-media-id]')?.getAttribute('data-media-id') === mediaId);
    if (index >= 0 && gallery.slideshow) gallery.slideshow.select(index, undefined, { animate: false });
  }

  /** @param {SubmitEvent} event */
  addToBag = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('ia-product__form')) return;
    event.preventDefault();
    if (this.busy || !form.reportValidity()) return;
    const button = form.querySelector('[data-ia-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const status = form.querySelector('[data-ia-status]');
    const stickyStatus = this.querySelector('[data-ia-sticky-status]');
    this.busy = true;
    for (const add of this.querySelectorAll('[data-ia-add]')) {
      if (add instanceof HTMLButtonElement) {
        add.disabled = true;
        add.setAttribute('aria-busy', 'true');
      }
    }
    for (const label of this.querySelectorAll('[data-ia-add-label]')) label.textContent = this.dataset.adding ?? '';
    if (status) status.textContent = '';
    if (stickyStatus) stickyStatus.textContent = '';
    for (const variant of this.querySelectorAll('[data-variant-id]')) {
      if (variant instanceof HTMLButtonElement) variant.disabled = true;
    }
    const data = new FormData(form);
    const sectionIds = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    }
    if (sectionIds.size) data.set('sections', [...sectionIds].join(','));
    data.set('sections_url', window.location.pathname);
    this.request?.abort();
    this.request = new AbortController();
    const timeout = window.setTimeout(() => this.request?.abort('timeout'), 20000);
    try {
      const response = await fetch(this.dataset.cartAddUrl ?? '/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data, signal: this.request.signal });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      this.dispatchEvent(new CartAddEvent({}, String(data.get('id')), { source: 'product-form-component', productId: this.dataset.productId, itemCount: Number(data.get('quantity')) || 1, sections: result.sections }));
      if (status) {
        status.textContent = this.dataset.added ?? '';
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl ?? '/cart';
        link.textContent = this.dataset.viewBag ?? 'View bag';
        status.appendChild(link);
      }
      if (stickyStatus) stickyStatus.textContent = this.dataset.added ?? '';
    } catch (error) {
      if (!this.isConnected) return;
      const timedOut = this.request.signal.reason === 'timeout';
      const message = timedOut ? this.dataset.error ?? '' : error instanceof Error ? error.message : this.dataset.error ?? '';
      if (status) status.textContent = message;
      if (stickyStatus) stickyStatus.textContent = message;
      this.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      window.clearTimeout(timeout);
      this.busy = false;
      for (const add of this.querySelectorAll('[data-ia-add]')) add.removeAttribute('aria-busy');
      for (const variant of this.querySelectorAll('[data-variant-id]')) {
        if (variant instanceof HTMLButtonElement) variant.disabled = false;
      }
      const select = this.querySelector('[data-ia-select]');
      if (select instanceof HTMLSelectElement) this.selectVariant(select, false);
    }
  };

  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
    this.galleryRequest?.abort();
    this.purchaseObserver?.disconnect();
    this.chatSpacingStyle?.remove();
    super.disconnectedCallback();
  }
}

if (!customElements.get('ia-fragrance-library')) customElements.define('ia-fragrance-library', FragranceLibrary);
if (!customElements.get('ia-fragrance-product')) customElements.define('ia-fragrance-product', FragranceProduct);
